import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowDownTrayIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import MultiSelect from '../../components/stats/MultiSelect';
import { formatMeasure, formatNumber, formatPeriod } from '../../utils/statsFormat';

const MAX_ROWS = 3;
const selectClass = 'block rounded-md border-gray-300 py-1.5 text-sm focus:border-primary-500 focus:ring-primary-500';

const PCT_MODES = [
  { key: 'none', label: 'Valeurs' },
  { key: 'total', label: '% du total' },
  { key: 'row', label: '% de la ligne' },
  { key: 'col', label: '% de la colonne' },
];
const EXPORT_FORMATS = [
  { key: 'xlsx', label: 'Excel' },
  { key: 'csv', label: 'CSV' },
  { key: 'pdf', label: 'PDF' },
];

/** Fond de cellule : une seule teinte, d'autant plus soutenue que la valeur est grande. */
const heat = (value, max) =>
  value && max ? { backgroundColor: `rgba(42, 120, 214, ${0.06 + 0.34 * Math.min(1, value / max)})` } : undefined;

/** Petit indicateur de variation N-1, coloré selon le sens. */
const Variation = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const color = pct > 0 ? 'text-green-700' : pct < 0 ? 'text-red-700' : 'text-gray-500';
  return <span className={`ml-1 text-xs font-medium ${color}`}>({pct > 0 ? '+' : ''}{formatNumber(pct, 1)} %)</span>;
};

const StatsPivot = ({ query, options, spec, onSpecChange }) => {
  const { rows, col, measures, sort, compare } = spec;
  const set = (patch) => onSpecChange({ ...spec, ...patch });

  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState('');
  const [pct, setPct] = useState('none');

  const measureLabel = useMemo(() => Object.fromEntries(options.measures.map((m) => [m.key, m.label])), [options]);
  const periodDims = useMemo(() => new Set(options.dimensions.filter((d) => d.period).map((d) => d.key)), [options]);
  const apiSpec = useMemo(
    () => ({ rows, cols: col ? [col] : [], measures, sort, compare }),
    [rows, col, measures, sort, compare]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.statistics
      .pivot(query, apiSpec)
      .then((res) => !cancelled && setTable(res.data.data))
      .catch((err) => !cancelled && setError(err.response?.data?.error || 'Impossible de calculer le tableau'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [query, apiSpec]);

  const setRow = (index, key) => {
    const next = [...rows];
    if (key) next[index] = key;
    else next.splice(index, 1);
    set({ rows: [...new Set(next)] });
  };

  const swap = () => {
    if (!col && rows.length === 0) return;
    set({ rows: col ? [col, ...rows.slice(1)] : rows.slice(1), col: rows[0] || '' });
  };

  const download = async (format) => {
    setExporting(format);
    try {
      const res = await api.statistics.exportPivot(query, apiSpec, format);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `statistiques-${new Date().toISOString().slice(0, 10)}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export impossible');
    } finally {
      setExporting('');
    }
  };

  const label = (dimKey, value) => (periodDims.has(dimKey) ? formatPeriod(value) : value);
  const free = (current) => options.dimensions.filter((d) => d.key === current || (!rows.includes(d.key) && d.key !== col));
  const first = table?.measures[0];
  const stickyTotal = table?.measures.length === 1 ? 'sticky right-0' : '';
  const maxCell = table && first && pct === 'none'
    ? Math.max(0, ...table.rows.flatMap((r) => (table.cols.length ? table.cols.map((c) => r.cells[c]?.[first.key] || 0) : [r.total[first.key] || 0])))
    : 0;

  // Sous-totaux : uniquement quand les lignes sont regroupées (tri alphabétique, 2+ axes).
  const grouped = !!table && table.rowDims.length >= 2 && sort === 'libelle' && (table.subtotals || []).length > 0;
  const subByKey = useMemo(
    () => new Map((table?.subtotals || []).map((s) => [s.key, s])),
    [table]
  );

  // Base d'un pourcentage selon le mode choisi.
  const pctBase = (mKey, { rowTotal, colKey }) => {
    if (pct === 'row') return rowTotal !== undefined ? rowTotal : table.grandTotal[mKey];
    if (pct === 'col') return colKey ? table.totals[colKey]?.[mKey] : table.grandTotal[mKey];
    return table.grandTotal[mKey];
  };
  const show = (value, m, ctx = {}) => {
    if (pct === 'none') return formatMeasure(value, m.unit);
    const base = pctBase(m.key, ctx);
    if (value === null || value === undefined || !base) return '—';
    return `${formatNumber((value / base) * 100, 1)} %`;
  };

  return (
    <div className="space-y-4">
      {/* Composition du tableau */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Lignes</p>
            <div className="flex flex-wrap items-center gap-2">
              {[...rows, ...(rows.length < MAX_ROWS ? [''] : [])].map((key, index) => (
                <select key={`${index}-${key}`} value={key} onChange={(e) => setRow(index, e.target.value)}
                  aria-label={`Axe de ligne ${index + 1}`} className={selectClass}>
                  <option value="">{index === 0 ? 'Aucun (total seul)' : '+ Ajouter un axe'}</option>
                  {free(key).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
              ))}
            </div>
          </div>

          <button type="button" onClick={swap} title="Inverser lignes et colonnes"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-600 hover:bg-gray-50">
            <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Inverser lignes et colonnes</span>
          </button>

          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Colonnes</p>
            <select value={col} onChange={(e) => set({ col: e.target.value })} aria-label="Axe de colonne" className={selectClass}>
              <option value="">Aucune</option>
              {options.dimensions.filter((d) => !rows.includes(d.key)).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>

          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Mesures</p>
            <MultiSelect label={measures.map((m) => measureLabel[m]).join(', ') || 'Choisir'}
              options={options.measures.map((m) => m.key)} value={measures}
              onChange={(next) => set({ measures: next.length ? next : ['quantite'] })} format={(k) => measureLabel[k]} />
          </div>

          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Tri des lignes</p>
            <select value={sort} onChange={(e) => set({ sort: e.target.value })} aria-label="Tri des lignes" className={selectClass}>
              <option value="valeur">Par valeur décroissante</option>
              <option value="libelle">Par ordre alphabétique</option>
            </select>
          </div>

          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Affichage</p>
            <select value={pct} onChange={(e) => setPct(e.target.value)} aria-label="Affichage en pourcentage" className={selectClass}>
              {PCT_MODES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input type="checkbox" checked={!!compare} onChange={(e) => set({ compare: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            Comparer à la période précédente
          </label>

          <div className="ml-auto flex items-end gap-1.5">
            {EXPORT_FORMATS.map((f) => (
              <button key={f.key} type="button" onClick={() => download(f.key)} disabled={!!exporting || !table}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                {exporting === f.key ? '…' : f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <p className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</p>}

      {table && !error && (
        <div className={`rounded-lg border border-gray-200 bg-white ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
          <p className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
            {table.rows.length} ligne{table.rows.length > 1 ? 's' : ''}
            {table.cols.length > 0 && ` × ${table.cols.length} colonne${table.cols.length > 1 ? 's' : ''}`}
            {table.truncated && ' — affichage limité aux 500 premières lignes ; l\'export Excel les contient toutes'}
            {pct === 'none' && first && ` — l'intensité du fond suit « ${first.label} »`}
            {table.rowDims.length >= 2 && sort !== 'libelle' && ' — passez en tri alphabétique pour voir les sous-totaux par groupe'}
            {compare && ` — variation vs période précédente sur « ${first?.label} »`}
          </p>
          <div className="max-h-[70vh] overflow-auto">
            <table className="min-w-full border-separate border-spacing-0 text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-xs text-gray-600">
                <tr>
                  {(table.rowDims.length ? table.rowDims : [{ key: '_', label: '' }]).map((d, k) => (
                    <th key={d.key} rowSpan={table.measures.length > 1 ? 2 : 1}
                      className={`border-b border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold ${k === 0 ? 'sticky left-0 z-20' : ''}`}>{d.label}</th>
                  ))}
                  {table.cols.map((c) => (
                    <th key={c} colSpan={table.measures.length}
                      className="whitespace-nowrap border-b border-l border-gray-200 px-3 py-2 text-right font-semibold">
                      {label(table.colDim.key, c)}
                    </th>
                  ))}
                  <th colSpan={table.measures.length}
                    className={`whitespace-nowrap border-b border-l border-gray-300 bg-gray-100 px-3 py-2 text-right font-semibold text-gray-900 ${stickyTotal}`}>
                    Total
                  </th>
                </tr>
                {table.measures.length > 1 && (
                  <tr>
                    {[...table.cols, '_total'].flatMap((c) => table.measures.map((m) => (
                      <th key={`${c}-${m.key}`}
                        className={`whitespace-nowrap border-b border-gray-200 px-3 py-1 text-right font-medium ${c === '_total' ? 'bg-gray-100' : ''}`}>
                        {m.label}
                      </th>
                    )))}
                  </tr>
                )}
              </thead>
              <tbody>
                {table.rows.length === 0 && (
                  <tr><td colSpan={99} className="px-3 py-8 text-center text-gray-400">Aucune donnée pour ces filtres</td></tr>
                )}
                {table.rows.map((row, r) => {
                  const previous = table.rows[r - 1];
                  // Sous-total du groupe précédent, inséré au changement de premier axe.
                  const subtotal = grouped && (!previous || previous.keys[0] !== row.keys[0]) && r > 0
                    ? subByKey.get(previous.keys[0]) : null;
                  const rowNode = (
                    <tr key={row.keys.join('|') || 'total'} className="hover:bg-gray-50">
                      {(table.rowDims.length ? row.keys : ['Total']).map((key, k) => {
                        const repeated = sort === 'libelle' && k < row.keys.length - 1 && previous
                          && previous.keys.slice(0, k + 1).join('|') === row.keys.slice(0, k + 1).join('|');
                        return (
                          <th key={k} scope="row"
                            className={`max-w-xs truncate border-b border-gray-100 bg-white px-3 py-1.5 text-left font-normal text-gray-900 ${k === 0 ? 'sticky left-0 z-[5]' : ''}`}
                            title={table.rowDims.length ? label(table.rowDims[k].key, key) : key}>
                            {repeated ? '' : table.rowDims.length ? label(table.rowDims[k].key, key) : key}
                          </th>
                        );
                      })}
                      {table.cols.flatMap((c) => table.measures.map((m, i) => (
                        <td key={`${c}-${m.key}`}
                          className={`whitespace-nowrap border-b border-gray-100 px-3 py-1.5 text-right tabular-nums text-gray-900 ${i === 0 ? 'border-l' : ''}`}
                          style={i === 0 ? heat(row.cells[c]?.[m.key], maxCell) : undefined}>
                          {row.cells[c] ? show(row.cells[c][m.key], m, { rowTotal: row.total[m.key], colKey: c }) : ''}
                        </td>
                      )))}
                      {table.measures.map((m, i) => (
                        <td key={m.key}
                          className={`whitespace-nowrap border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-right font-medium tabular-nums text-gray-900 ${i === 0 ? 'border-l border-l-gray-300' : ''} ${stickyTotal}`}
                          style={i === 0 && table.cols.length === 0 && pct === 'none' ? heat(row.total[m.key], maxCell) : undefined}>
                          {show(row.total[m.key], m, { rowTotal: row.total[m.key] })}
                          {compare && i === 0 && row.variation && <Variation pct={row.variation[m.key]} />}
                        </td>
                      ))}
                    </tr>
                  );
                  if (!subtotal) return rowNode;
                  return (
                    <React.Fragment key={`grp-${previous.keys[0]}-${r}`}>
                      <SubtotalRow subtotal={subtotal} table={table} pct={pct} show={show} />
                      {rowNode}
                    </React.Fragment>
                  );
                })}
                {/* Sous-total du dernier groupe */}
                {grouped && table.rows.length > 0 && subByKey.get(table.rows[table.rows.length - 1].keys[0]) && (
                  <SubtotalRow subtotal={subByKey.get(table.rows[table.rows.length - 1].keys[0])} table={table} pct={pct} show={show} last />
                )}
              </tbody>
              <tfoot className="sticky bottom-0 bg-gray-100 text-gray-900">
                <tr>
                  <th scope="row" colSpan={Math.max(1, table.rowDims.length)}
                    className="sticky left-0 border-t border-gray-300 bg-gray-100 px-3 py-2 text-left font-semibold">Total général</th>
                  {table.cols.flatMap((c) => table.measures.map((m, i) => (
                    <td key={`${c}-${m.key}`}
                      className={`whitespace-nowrap border-t border-gray-300 px-3 py-2 text-right font-semibold tabular-nums ${i === 0 ? 'border-l border-l-gray-200' : ''}`}>
                      {show(table.totals[c][m.key], m, { rowTotal: table.grandTotal[m.key], colKey: c })}
                    </td>
                  )))}
                  {table.measures.map((m, i) => (
                    <td key={m.key}
                      className={`whitespace-nowrap border-t border-gray-300 bg-gray-100 px-3 py-2 text-right font-bold tabular-nums ${i === 0 ? 'border-l border-l-gray-300' : ''} ${stickyTotal}`}>
                      {show(table.grandTotal[m.key], m, { rowTotal: table.grandTotal[m.key] })}
                      {compare && i === 0 && table.grandComparison && <Variation pct={table.grandComparison.variation[m.key]} />}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {!table && !error && (
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-primary-600" />
        </div>
      )}
    </div>
  );
};

/** Ligne de sous-total d'un groupe (premier axe de ligne). */
const SubtotalRow = ({ subtotal, table, pct, show }) => (
  <tr className="bg-primary-50/60">
    <th scope="row" colSpan={table.rowDims.length}
      className="sticky left-0 border-b border-gray-200 bg-primary-50 px-3 py-1.5 text-left text-xs font-semibold text-primary-900">
      Sous-total — {subtotal.key}
    </th>
    {table.cols.flatMap((c) => table.measures.map((m, i) => (
      <td key={`${c}-${m.key}`}
        className={`whitespace-nowrap border-b border-gray-200 bg-primary-50 px-3 py-1.5 text-right font-semibold tabular-nums text-primary-900 ${i === 0 ? 'border-l' : ''}`}>
        {subtotal.cells[c] ? show(subtotal.cells[c][m.key], m, { rowTotal: subtotal.total[m.key], colKey: c }) : ''}
      </td>
    )))}
    {table.measures.map((m, i) => (
      <td key={m.key}
        className={`whitespace-nowrap border-b border-gray-200 bg-primary-100 px-3 py-1.5 text-right font-semibold tabular-nums text-primary-900 ${i === 0 ? 'border-l border-l-gray-300' : ''}`}>
        {show(subtotal.total[m.key], m, { rowTotal: subtotal.total[m.key] })}
      </td>
    ))}
  </tr>
);

export default StatsPivot;
